import { useMemo } from 'react';

import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { type DateTimeRangeValidationError } from '@packages/fenrir/components/date-pickers';

interface DateTimeRangePickerError {
  error: DateTimeRangeValidationError | null;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

export const useDateTimeRangePickerErrorMessages = ({
  error,
  minDate,
  maxDate,
}: DateTimeRangePickerError) => {
  const { t } = useTranslation();

  const errorMessages: Record<string, string> = useMemo(() => {
    const formattedMinDate = minDate
      ? dayjs(minDate).format('MM/DD/YYYY')
      : undefined;

    const formattedMaxDate = maxDate
      ? dayjs(maxDate).format('MM/DD/YYYY')
      : undefined;

    return {
      maxDate: formattedMaxDate
        ? t('error:datePicker.maxDateMessage', {
            maxDate: formattedMaxDate,
          })
        : t('error:datePicker.invalidEndDate'),
      minDate: formattedMinDate
        ? t('error:datePicker.minDateMessage', {
            minDate: formattedMinDate,
          })
        : t('error:datePicker.invalidStartDate'),
      invalidRange: t('error:datePicker.invalidDateRange'),
      invalidDate: t('error:datePicker.invalidDate'),
    };
  }, [minDate, maxDate, t]);

  return useMemo(() => {
    const [startError, endError] = error ?? [];

    return {
      start: startError ? errorMessages[startError] : '',
      end: endError ? errorMessages[endError] : '',
    };
  }, [error, errorMessages]);
};
